import { ServerError } from '@apollo/client'
import { KeyboardEvent as ReactKeyboardEvent } from 'react'

import styled, { MQ, MQ_TABLET } from '../../constants/theme'
import { FormElementCallback, FormErrors } from '../../hooks/useForm'
import { ValidationFunction } from './Validations'

// these are only really used for auth and registration forms
export enum BasicType {
  FOOTER = 'footer',
  PLAINTEXT = 'plaintext',
  SUBMIT = 'submit',
}

export enum ElementType {
  AUTOCOMPLETE = 'autocomplete',
  BUTTON = 'button',
  CHECKBOX = 'checkbox',
  DATE = 'date',
  DECIMAL = 'decimal',
  DOB = 'dob',
  DURATION = 'duration',
  EMAIL = 'email',
  HIDDEN = 'hidden',
  INTEGER = 'integer',
  NUMBER = 'number', // used as HTML "type" attribute for DECIMAL and INTEGER types
  PASSWORD = 'password',
  PHARMACY_SELECT = 'pharmacy_select',
  RADIO = 'radio',
  SELECT = 'select',
  SWITCH = 'switch',
  TEL = 'tel',
  TEXT = 'text',
  TEXTAREA = 'textarea',
  ZIP = 'zipcode',
}

export enum QuestionType {
  MULTISELECT = 'multiselect',
}

export enum ElementState {
  INDETERMINATE = 'INDETERMINATE',
  INVALID = 'INVALID',
  REQUEST = 'REQUEST',
  SUBMITTED = 'SUBMITTED',
  VALID = 'VALID',
}

export interface Field {
  cancelLabel?: string
  description?: string
  display?: string
  falsyValues?: string[]
  fontSize?: number
  hint?: { header: string; image?: string; text?: string }
  icon?: string
  label: string
  largeMarginTop?: boolean
  modal?: { body: string; header: string; trackEventName?: string }
  name?: string
  options?: SelectOption[]
  persistError?: boolean
  placeholder?: string
  required?: boolean
  serverErrors?: ServerError
  submitOverride?: { pattern: string; replace: string }
  textAlign?: 'center' | 'left' | 'right'
  triggerLabel?: string
  type: BasicType | ElementType | QuestionType
  validPattern?: string
  value?: string
}

export interface FormElementProps {
  ariaLabelledBy?: string
  disabled?: boolean
  display?: string
  description?: string
  elementState?: ElementState
  errorText?: string
  icon?: JSX.Element
  id?: string
  includeBlank?: string
  innerRef?: (instance: unknown) => void
  isFocused?: boolean
  isRequired?: boolean
  label?: string
  multiSelect?: boolean
  name?: string
  onChange?: (e: any) => void
  onEdit?: () => void
  onFormElementChange?: FormElementCallback
  onInput?: (e: any) => void
  onKeyDown?: (e: ReactKeyboardEvent) => void
  onKeyUp?: (e: any) => void
  onPaste?: (e: any) => void
  options?: SelectOption[]
  pattern?: string
  persistError?: boolean
  placeholder?: string | null
  requiresPassword?: boolean
  rightButton?: JSX.Element
  rightIcon?: JSX.Element
  serverErrors?: FormErrors | null
  showCharsRemaining?: number
  showFieldIcons?: boolean
  spellCheck?: boolean
  step?: number
  type: BasicType | ElementType | QuestionType
  unifiedErrorText?: string
  validations?: ValidationFunction[]
  value?: string | string[] | null
}

export interface CommonFormElementProps extends FormElementProps {
  key: string
}

export interface SelectOption {
  label: string
  value: number | string
  translatedLabel?: string
}

export interface FormElementState {
  elementState: ElementState
  serverErrors?: FormErrors | null
  value?: string | string[] | null
}

export const EditAvatar = styled.div(({ theme }) => ({
  flex: '0 0 154px',
  height: 154,
  margin: `${theme.paddingSmall}px ${theme.padding}px 0 0`,
  width: 154,
}))

export const Column = styled.div(({ theme }) => ({
  width: '100%',
  '& p:first-of-type + div > label': {
    marginTop: theme.paddingSmall,
  },
}))

export const Label = styled.label(({ theme }) => ({
  display: 'flex',
  fontSize: 12,
  fontWeight: 'bold',
  marginBottom: 4,
  marginTop: theme.paddingMobile,
}))

const sharedRightStyles: any = {
  bottom: 0,
  position: 'absolute',
  right: 0,
  transform: 'scale(0.7)',
}

export const RightButtonContainer = styled.div(
  sharedRightStyles,
  ({ theme }) => ({
    fill: theme.text,
  }),
)

export const RightIconContainer = styled.div(
  {
    ...sharedRightStyles,
    pointerEvents: 'none',
  },
  ({ theme }) => ({
    fill: theme.text,
  }),
)

export const FlexRow = styled.div({
  ...MQ_TABLET({
    '.SettingsMedical &, &.AccountDetails, &.MoreInformation, &.Profile, &.Profile &':
      {
        display: 'block',
      },
    display: 'flex',
  }),
  [MQ.LARGE_DESKTOP]: {
    'html[style*="1rem"] .SettingsMedical &, html[style*="1rem"] &.AccountDetails, html[style*="1rem"] &.MoreInformation, html[style*="1rem"] &.Profile, html[style*="1rem"] &.Profile &':
      {
        display: 'flex',
      },
  },
  [MQ.X_LARGE_DESKTOP]: {
    'html[style*="px"] .SettingsMedical &, html[style*="px"] &.AccountDetails, html[style*="px"] &.MoreInformation, html[style*="px"] &.Profile, html[style*="px"] &.Profile &':
      {
        display: 'flex',
      },
  },
  '& > div:first-of-type:not(:last-of-type)': {
    marginBottom: 15,
    marginRight: 20,
  },
  display: 'block',
  marginBottom: 10,
  width: '100%',
})

export const PillDiv = styled.div({
  textAlign: 'right',
})
